/**
 * Transforme les données de domaines brutes de Strapi en structure utilisable par le MegaMenu
 * Organisé par grandes catégories (Products, Industries, Services, etc.) regroupées par domaine
 *
 * @param {Array} domains - Les données de domaines provenant de Strapi
 * @returns {Array} - Structure optimisée pour le MegaMenu
 */
export const transformDomainsToMegaMenuStructure = (domains) => {
  if (!domains || !Array.isArray(domains)) {
    return [];
  }

  // Regrouper les données par type (Products, Industries, Services, etc.)
  const groupedByType = {
    companies: {},
    products: {},
    industries: {},
    services: {},
    rds: {}
  };

  domains.forEach(domain => {
    if (domain.products && domain.products.length > 0) {
      groupedByType.products[domain.documentId] = {
        domain: domain,
        items: domain.products
      };
    }

    if (domain.industries && domain.industries.length > 0) {
      groupedByType.industries[domain.documentId] = {
        domain: domain,
        items: domain.industries
      };
    }

    if (domain.services && domain.services.length > 0) {
      groupedByType.services[domain.documentId] = {
        domain: domain,
        items: domain.services
      };
    }

    if (domain.rds && domain.rds.length > 0) {
      groupedByType.rds[domain.documentId] = {
        domain: domain,
        items: domain.rds
      };
    }

    if (domain.companies && domain.companies.length > 0) {
      groupedByType.companies[domain.documentId] = {
        domain: domain,
        items: domain.companies
      };
    }
  });

  // Convertir en structure pour le MegaMenu
  const megaMenuStructure = [];

  // Ordre explicite des catégories (companies en premier)
  const categoryOrder = ['companies', 'products', 'services', 'industries', 'rds'];

  // Ajouter les catégories avec des éléments dans l'ordre défini
  categoryOrder.forEach(type => {
    const domainGroupsByDocumentId = groupedByType[type];
    if (domainGroupsByDocumentId && Object.keys(domainGroupsByDocumentId).length > 0) {
      const categoryTitle =
        type === 'companies' ? 'ABOUT US' :
        type === 'products' ? 'PRODUCTS' :
        type === 'industries' ? 'INDUSTRIES' :
        type === 'services' ? 'SERVICES' : 'R&D';

      // Slug imposé pour l'URL
      const categorySlugFr =
        type === 'companies' ? 'company' :
        type === 'products' ? 'produits' :
        type === 'industries' ? 'industries' :
        type === 'services' ? 'services' : 'r-and-d';

      // Créer une structure qui regroupe les éléments par domaine
      const groupedItems = [];
      Object.values(domainGroupsByDocumentId).forEach(group => {
        group.items.forEach(item => {
          groupedItems.push({
            ...item,
            parentDomain: group.domain.title,
            parentDomainSlug: group.domain.slug,
            parentDomainColor: group.domain.color,
            parentDomainIcon: group.domain.icon,
            type: type
          });
        });
      });

      megaMenuStructure.push({
        id: type,
        title: categoryTitle,
        slug: type, // Utiliser le slug anglais pour la requête
        slugFr: categorySlugFr, // Ajouter le slug français pour l'URL
        type: type,
        submenus: groupedItems
      });
    }
  });

  return megaMenuStructure;
};

/**
 * Transforme les données de domaines brutes de Strapi en structure pour le MegaMenu
 * avec regroupement des éléments par domaine au sein de chaque catégorie
 *
 * @param {Array} domains - Les données de domaines provenant de Strapi
 * @returns {Array} - Structure optimisée pour le MegaMenu avec regroupement par domaine
 */
export const transformDomainsToGroupedMegaMenuStructure = (domains) => {
  if (!domains || !Array.isArray(domains)) {
    return [];
  }

  // Regrouper les données par type (Products, Industries, Services, etc.)
  const groupedByType = {
    companies: {},
    products: {},
    industries: {},
    services: {},
    rds: {}
  };

  domains.forEach(domain => {
    if (domain.products && domain.products.length > 0) {
      groupedByType.products[domain.documentId] = {
        domain: domain,
        items: domain.products
      };
    }

    if (domain.industries && domain.industries.length > 0) {
      groupedByType.industries[domain.documentId] = {
        domain: domain,
        items: domain.industries
      };
    }

    if (domain.services && domain.services.length > 0) {
      groupedByType.services[domain.documentId] = {
        domain: domain,
        items: domain.services
      };
    }

    if (domain.rds && domain.rds.length > 0) {
      groupedByType.rds[domain.documentId] = {
        domain: domain,
        items: domain.rds
      };
    }

    if (domain.companies && domain.companies.length > 0) {
      groupedByType.companies[domain.documentId] = {
        domain: domain,
        items: domain.companies
      };
    }
  });

  // Convertir en structure pour le MegaMenu
  const megaMenuStructure = [];

  // Ordre explicite des catégories (companies en premier)
  const categoryOrder = ['companies', 'products', 'services', 'industries', 'rds'];

  // Ajouter les catégories avec des éléments dans l'ordre défini
  categoryOrder.forEach(type => {
    const domainGroupsByDocumentId = groupedByType[type];
    if (domainGroupsByDocumentId && Object.keys(domainGroupsByDocumentId).length > 0) {
      const categoryTitle =
        type === 'companies' ? 'ABOUT US' :
        type === 'products' ? 'PRODUCTS' :
        type === 'industries' ? 'INDUSTRIES' :
        type === 'services' ? 'SERVICES' : 'R&D';

      // Slug imposé pour l'URL
      const categorySlugFr =
        type === 'companies' ? 'company' :
        type === 'products' ? 'produits' :
        type === 'industries' ? 'industries' :
        type === 'services' ? 'services' : 'r-and-d';

      // Créer une structure qui regroupe les éléments par domaine
      const groupedItems = [];
      Object.values(domainGroupsByDocumentId).forEach(group => {
        group.items.forEach(item => {
          groupedItems.push({
            ...item,
            parentDomain: group.domain.title,
            parentDomainSlug: group.domain.slug,
            parentDomainColor: group.domain.color,
            parentDomainIcon: group.domain.icon,
            type: type
          });
        });
      });

      megaMenuStructure.push({
        id: type,
        title: categoryTitle,
        slug: type, // Utiliser le slug anglais pour la requête
        slugFr: categorySlugFr, // Ajouter le slug français pour l'URL
        type: type,
        submenus: groupedItems
      });
    }
  });

  return megaMenuStructure;
};

/**
 * Fonction utilitaire pour obtenir une structure inversée : par domaines avec leurs catégories
 */
export const transformDomainsToSimpleMegaMenuStructure = (domains) => {
  if (!domains || !Array.isArray(domains)) {
    return [];
  }

  return domains.map(domain => ({
    id: domain.id,
    documentId: domain.documentId,
    title: domain.title,
    slug: domain.slug,
    description: domain.description,
    color: domain.color,
    icon: domain.icon,
    submenus: [
      ...(domain.products || []).map(product => ({
        id: product.id,
        documentId: product.documentId,
        title: product.title,
        slug: product.slug,
        description: product.metaDescription || product.description || '',
        type: 'product',
        parentDomain: domain.title
      })),
      ...(domain.services || []).map(service => ({
        id: service.id,
        documentId: service.documentId,
        title: service.title,
        slug: service.slug,
        description: service.description || '',
        type: 'service',
        parentDomain: domain.title
      })),
      ...(domain.industries || []).map(industry => ({
        id: industry.id,
        documentId: industry.documentId,
        title: industry.title,
        slug: industry.slug,
        description: industry.description || '',
        type: 'industry',
        parentDomain: domain.title
      })),
      ...(domain.rds || []).map(rd => ({
        id: rd.id,
        documentId: rd.documentId,
        title: rd.title,
        slug: rd.slug,
        description: rd.description || '',
        type: 'research',
        parentDomain: domain.title
      })),
      ...(domain.companies || []).map(company => ({
        id: company.id,
        documentId: company.documentId,
        title: company.title,
        slug: company.slug,
        description: company.description || '',
        type: 'company',
        parentDomain: domain.title
      }))
    ],
    hasSubcategories: (domain.products || []).length > 0 ||
                     (domain.services || []).length > 0 ||
                     (domain.industries || []).length > 0 ||
                     (domain.rds || []).length > 0 ||
                     (domain.companies || []).length > 0
  }));
};